import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./login.scss";
import { BrandButton } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { useContext } from "react";
import { AppDataContext } from "../components/ctx";
import { ctxDefinition } from "../components/ctx";
import { z } from "zod";
import { Snackbar, Alert } from "@mui/material";
import { ApiEndpoint } from "../components/utils";
import { pageIds } from "../components/utils";
import ChooseExamType from "../components/chooseExamType";
import NetworkError from "../components/networkError";
import BackButton from "../components/backBtn";

interface PageResponse {
  headerTitle?: string;
  welcomeMessage?: any;
  loginFooter: any;
}

interface ConfigResponse {
  questionSections: string[];
  standardTime: number[];
  extendedTime: number[];
}

const Login = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [snackError, setSnackError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showExamTypeModal, setShowExamTypeModal] = useState<boolean>(false);
  const homeData = useRef(null);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const notEmptyString = z.string().min(1);
  const vertical = "bottom";
  const horizontal = "center";
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field has to be filled." })
      .email("This is not a valid email."),
  });
  const {
    setLoggedIn,
    userData,
    setUserData,
    setConfig,
    setExamStartTime,
    setExamStarted,
    setExamType,
    fetchSectionQuestions,
  } = useContext(AppDataContext) as ctxDefinition;
  const username = useRef("");
  const password = useRef("");
  const [networkError, setNetworkError] = useState(false);

  const { isLoading, error } = useQuery("registerPageData", async () => {
    try {
      const resp = await axios.get(
        ApiEndpoint +
          "wp-json/firstchoiceprep/v1/getPageText?slug=" +
          pageIds.login
      );
      // TODO - on the server make exam type null by default and update to selected type that user chose.
      setdata(resp.data as PageResponse);
    } catch (e) {
      setNetworkError(true);
    }
  });

  const checkRetryAttempts = (uData: any): boolean => {
    return uData.retryAttempts < 0 ? true : false;
  };

  const validateForm = async () => {
    const checkEmail = validEmail.safeParse({ email: username.current });
    const checkPassword = notEmptyString.safeParse(password.current);
    if (!checkEmail.success || !checkPassword.success) {
      setErrorMsg(
        "Email or password field are in incorrect format. Please try again."
      );
      setSnackError(true);
    } else {
      try {
        const resp = await axios.post(
          ApiEndpoint + "wp-json/firstchoiceprep/v1/login",
          {
            email: username.current,
            password: password.current,
          }
        );

        if (resp.data) {
          const success: any = resp.data.success;
          if (success) {
            setLoggedIn(true);

            const udata: any = await axios.get(
              ApiEndpoint +
                "wp-json/firstchoiceprep/v1/getUserData?token=" +
                resp.data.token
            ); // TODO needs to be taken from server

            const resp2 = await axios.get(
              ApiEndpoint +
                "wp-json/firstchoiceprep/v1/configData?token=" +
                resp.data.token
            );
            setConfig(resp2.data as ConfigResponse);

            if (resp.data.paid === false) {
              navigate("/register-coupon", {
                replace: false,
                state: {
                  token: resp.data.token,
                  userid: resp.data.userid,
                  userData: udata.data,
                  username: username.current,
                },
              });
              return;
            }

            udata.data.retryAttempts--;
            await axios.post(
              ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
              {
                token: udata.data.token,
                fields: ["retryAttempts"],
                values: [udata.data.retryAttempts],
              }
            );

            udata.data.isadmin = resp.data.isadmin;
            udata.data.token = resp.data.token;
            setUserData(udata.data);

            if (resp.data.isadmin) {
              navigate("/exam-results", { replace: true });
              return;
            }

            if (udata.data.examComplete) {
              navigate("/exam-results", { replace: true });
            } else if (!udata.data.examStarted) {
              if (checkRetryAttempts(udata.data)) {
                navigate("/max-retry", { replace: true });
              } else {
                homeData.current = await axios.get(
                  ApiEndpoint +
                    "wp-json/firstchoiceprep/v1/getPageText?slug=" +
                    pageIds.homepage
                );
                setShowExamTypeModal(true);
              }
            } else {
              // if the user logins and is in the middle of a question
              // but no time is set...set a time...
              if (!udata.data.sectionStartTime) {
                udata.data.sectionStartTime = new Date().getTime();
                await axios.post(
                  ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
                  {
                    token: udata.data.token,
                    fields: ["sectionStartTime"],
                    values: [udata.data.sectionStartTime],
                  }
                );
                setUserData(udata.data);
              }
              setExamType(udata.data.examType);
              setExamStartTime(udata.data.sectionStartTime);
              setExamStarted(true);

              // Fetch all questions for the current section
              const sectionName =
                resp2.data.questionSections[udata.data.currentSectionId];
              console.log(
                "Login - Fetching questions for section:",
                sectionName,
                "at index:",
                udata.data.currentSectionId
              );
              const questions = await fetchSectionQuestions(
                udata.data.token,
                sectionName
              );

              if (!questions) {
                console.error(
                  "Login - Failed to fetch questions for section:",
                  sectionName
                );
                setHasError(true);
                return;
              }

              console.log(
                "Login - Successfully fetched",
                questions.length,
                "questions"
              );

              let currQuestion = udata.data.lastQuestionAnswered;
              if (!currQuestion) {
                currQuestion =
                  udata.data.examAnswers[udata.data.currentSectionId]
                    .questions[0].questionId;
              }
              navigate("/question/" + currQuestion, {
                replace: true,
              });
            }
          } else {
            setErrorMsg(resp.data.message);
            setSnackError(true);
            return;
          }
        } else {
          setErrorMsg("Error logging in. Please try again.");
          setSnackError(true);
          return;
        }
        return;
      } catch (e) {
        setErrorMsg("Error logging in. Please try again.");
        setSnackError(true);
      }
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  const updateExamType = async (target: number) => {
    setHasError(false);
    const uData = { ...userData };
    uData.examType = target;
    try {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
        {
          token: userData.token,
          fields: ["examType"],
          values: [`${target}.0`],
        }
      );
      if (!resp) {
        setShowExamTypeModal(false);
        setHasError(true);
      } else {
        setExamType(target);
        setUserData(uData);
        navigate("/exam-landing", { replace: true });
      }
    } catch (e) {
      setShowExamTypeModal(false);
      setHasError(true);
    }
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        validateForm();
      }
    };
    document.addEventListener("keydown", onEnter);
    return () => {
      document.removeEventListener("keydown", onEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (networkError) return <NetworkError open={true} />;

  if (isLoading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="loginPage">
      <BackButton navigate={navigate} destination="/" text="home" />
      <div className="headline">
        <h1>{data?.headerTitle}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: data?.welcomeMessage }}
        />
      </div>
      <div className="loginForm">
        <div className="formRow">
          <BrandInput
            onChange={(evt) => (username.current = evt.currentTarget.value)}
            id="filled-basic"
            label="Student’s Email"
            variant="filled"
          />
          <BrandInput
            onChange={(evt) => (password.current = evt.currentTarget.value)}
            id="filled-basic" // corrected id here
            label="Student’s Password"
            type="password"
            variant="filled"
          />
          <div className="btnHolder">
            <BrandButton onClick={validateForm}>LOGIN</BrandButton>
          </div>
        </div>
        <div className="formRow forgotPassword">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
        <div className="loginMeta">
          <div className="registerCallout">
            <span>Don't have an account?</span>
            <BrandButton
              variant="contained"
              onClick={() => navigate("/register", { replace: true })}
            >
              REGISTER
            </BrandButton>
          </div>
          <div
            className="loginFooter"
            dangerouslySetInnerHTML={{ __html: data?.loginFooter }}
          />
        </div>
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
      <ChooseExamType
        open={showExamTypeModal}
        setExamType={updateExamType}
        data={homeData.current}
      />
      <NetworkError open={hasError} />
    </div>
  );
};

export default Login;
